import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReferencePhotoRow from './ReferencePhotoRow';
import SelfieCaptureDialog from './SelfieCaptureDialog';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Container, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, LinearProgress, Snackbar, Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import PersonIcon from '@mui/icons-material/Person';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import SearchIcon from '@mui/icons-material/Search';
import { motion } from 'framer-motion';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PRIVACY_POLICY_VERSION } from '../../config/appConfig';
import profileApi from '../Profile/profileApi';
import eventApi from './eventApi';
import MarkdownContent from './EventMarkdown';
import './EventGallery.css';

const MAX_SIZE_BYTES = 10 * 1024 * 1024;

const PHOTO_COLORS = ['#f48fb1', '#9b59b6', '#27ae60', '#e67e22', '#4a90d9', '#e74c3c'];

const COUNTRY_OPTIONS = [
  { value: '+1', label: '+1 US' }, { value: '+44', label: '+44 UK' },
  { value: '+91', label: '+91 IN' }, { value: '+61', label: '+61 AU' },
  { value: '+971', label: '+971 UAE' }, { value: '+65', label: '+65 SG' },
  { value: '+60', label: '+60 MY' }, { value: '+27', label: '+27 ZA' },
  { value: '+33', label: '+33 FR' }, { value: '+49', label: '+49 DE' },
];

function extractInviteCodeFromQr(rawValue) {
  const value = String(rawValue || '').trim();
  if (!value) return '';
  const joinPathMatch = value.match(/\/join-event\/(\d{8})(?:\D|$)/i);
  if (joinPathMatch) return joinPathMatch[1];
  try {
    const url = new URL(value);
    const codeFromPath = url.pathname.match(/\/join-event\/(\d{8})(?:\D|$)/i);
    if (codeFromPath) return codeFromPath[1];
    const codeFromQuery = url.searchParams.get('code') || '';
    if (/^\d{8}$/.test(codeFromQuery)) return codeFromQuery;
  } catch { /* not a URL */ }
  const plainCodeMatch = value.match(/(?:^|\D)(\d{8})(?:\D|$)/);
  return plainCodeMatch ? plainCodeMatch[1] : '';
}

export default function JoinEvent({ initialMode = 'photos' }) {
  const navigate = useNavigate();
  const { code: routeCode } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanTimerRef = useRef(null);
  const qrImageInputRef = useRef(null);

  const cleanedInitialCode = (routeCode || searchParams.get('code') || '').replace(/\D/g, '').slice(0, 8);
  const cameViaLink = cleanedInitialCode.length === 8;

  const [inviteCode, setInviteCode] = useState(cleanedInitialCode);
  const [name, setName] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+44');
  const [contactType, setContactType] = useState('phone');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [whatsappCountryCode, setWhatsappCountryCode] = useState('+44');
  const [notes, setNotes] = useState('');
  const [referencePhoto, setReferencePhoto] = useState(null);
  const [secondaryPhotos, setSecondaryPhotos] = useState([]); // [{photo_key, url, file}]
  const [referenceConsent, setReferenceConsent] = useState(false);
  const [onBehalfRelationship, setOnBehalfRelationship] = useState('');
  const [onBehalfExpanded, setOnBehalfExpanded] = useState(false);
  const [subjectName, setSubjectName] = useState('');
  const [requestMode, setRequestMode] = useState(initialMode === 'deletion' ? 'deletion' : 'photos');
  const [bulkPhotos, setBulkPhotos] = useState([]);
  const [bulkProgress, setBulkProgress] = useState({ active: false, completed: 0, total: 0, failed: 0 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedCount, setSubmittedCount] = useState(1);
  const [submittedEventId, setSubmittedEventId] = useState(null);
  const [submittedEventName, setSubmittedEventName] = useState('');
  const [submittedEventAgenda, setSubmittedEventAgenda] = useState('');
  const [submittedHasSelfie, setSubmittedHasSelfie] = useState(true);
  const [showAccountPrompt, setShowAccountPrompt] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerStatus, setScannerStatus] = useState('');
  const [selfieCamOpen, setSelfieCamOpen] = useState(false);
  const [eventLookupState, setEventLookupState] = useState(cameViaLink ? 'loading' : 'idle');
  const [lookedUpEvent, setLookedUpEvent] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const pushToast = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message: String(message || ''), severity });
  }, []);

  const triggerEventLookup = useCallback(async (code) => {
    const cleaned = String(code || '').replace(/\D/g, '').slice(0, 8);
    if (!/^\d{8}$/.test(cleaned)) { pushToast('Invite code must be exactly 8 digits.', 'error'); return; }
    setEventLookupState('loading');
    setLookedUpEvent(null);
    try {
      const info = await eventApi.lookupEvent(cleaned);
      if (info.error) { setEventLookupState('error'); }
      else { setLookedUpEvent(info); setEventLookupState('found'); setInviteCode(cleaned); }
    } catch { setEventLookupState('error'); }
  }, [pushToast]);

  function resetEventLookup() {
    setLookedUpEvent(null);
    setEventLookupState('idle');
    setInviteCode('');
  }

  useEffect(() => {
    if (cameViaLink) triggerEventLookup(cleanedInitialCode);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const nextCode = (routeCode || searchParams.get('code') || '').replace(/\D/g, '').slice(0, 8);
    if (nextCode) setInviteCode(nextCode);
  }, [routeCode, searchParams]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setName(c => c || user.customer_name || '');
    setEmailAddress(c => c || user.email || '');
    profileApi.getProfile()
      .then(profile => {
        if (cancelled || !profile) return;
        setName(c => c || profile.customer_name || '');
        setEmailAddress(c => c || profile.email || '');
        setPhoneNumber(c => c || profile.phone || profile.extended?.alternate_phone || '');
        if (profile.country_code) setCountryCode(profile.country_code);
      })
      .catch(() => { if (!cancelled) setPhoneNumber(c => c || user.phone || user.phonenumber || ''); });
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (submitted) window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [submitted]);

  const previewUrl = useMemo(() => referencePhoto ? URL.createObjectURL(referencePhoto) : '', [referencePhoto]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  useEffect(() => {
    if (!scannerOpen) return undefined;
    let cancelled = false;
    let detecting = false;
    async function startScanner() {
      if (!navigator.mediaDevices?.getUserMedia) { setScannerStatus('Camera access not available. Enter the invite code manually.'); return; }
      if (!window.BarcodeDetector) { setScannerStatus('QR scanning not supported in this browser. Enter the invite code manually.'); return; }
      try {
        setScannerStatus('Starting camera...');
        const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
        setScannerStatus('Point your camera at the event QR code.');
        scanTimerRef.current = window.setInterval(async () => {
          if (detecting || !videoRef.current || videoRef.current.readyState < 2) return;
          detecting = true;
          try {
            const barcodes = await detector.detect(videoRef.current);
            const code = barcodes.map(b => extractInviteCodeFromQr(b.rawValue)).find(Boolean);
            if (code) { setInviteCode(code); pushToast('Invite code scanned.', 'success'); setScannerOpen(false); triggerEventLookup(code); }
          } catch { setScannerStatus('Still looking...'); } finally { detecting = false; }
        }, 500);
      } catch (err) {
        setScannerStatus(err.name === 'NotAllowedError' ? 'Camera permission denied. Enter the invite code manually.' : 'Could not start camera. Enter the invite code manually.');
      }
    }
    startScanner();
    return () => {
      cancelled = true;
      if (scanTimerRef.current) { window.clearInterval(scanTimerRef.current); scanTimerRef.current = null; }
      if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    };
  }, [scannerOpen, pushToast, triggerEventLookup]);

  function applyScannedInviteCode(rawValue, sourceLabel = 'QR code') {
    const code = extractInviteCodeFromQr(rawValue);
    if (!code) return false;
    setInviteCode(code); pushToast(`${sourceLabel} scanned.`, 'success'); setScannerOpen(false); triggerEventLookup(code);
    return true;
  }

  async function scanQrImageFile(file) {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setScannerStatus('Select an image file containing the QR code.'); return; }
    if (!window.BarcodeDetector) { setScannerStatus('QR image scanning not supported. Enter the code manually.'); return; }
    setScannerStatus('Reading QR code from image...');
    let imageSource = null; let objectUrl = '';
    try {
      const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
      if (window.createImageBitmap) { imageSource = await createImageBitmap(file); }
      else {
        objectUrl = URL.createObjectURL(file);
        imageSource = await new Promise((resolve, reject) => { const img = new Image(); img.onload = () => resolve(img); img.onerror = reject; img.src = objectUrl; });
      }
      const barcodes = await detector.detect(imageSource);
      const found = barcodes.some(b => applyScannedInviteCode(b.rawValue, 'QR image'));
      if (!found) setScannerStatus('No QR code found in that image. Try another or enter the code manually.');
    } catch { setScannerStatus('Could not read QR image. Try another or enter the code manually.'); }
    finally {
      if (imageSource?.close) imageSource.close();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      if (qrImageInputRef.current) qrImageInputRef.current.value = '';
    }
  }

  function onFileSelected(file) {
    if (!file) return;
    if (!file.type.startsWith('image/')) { pushToast('Please upload an image file.', 'error'); return; }
    if (file.size > MAX_SIZE_BYTES) { pushToast('Image must be less than 10 MB.', 'error'); return; }
    setReferencePhoto(file);
  }

  function onSecondaryFileAdded(file) {
    if (!file) return;
    if (!file.type.startsWith('image/')) { pushToast('Please upload an image file.', 'error'); return; }
    if (file.size > MAX_SIZE_BYTES) { pushToast('Image must be less than 10 MB.', 'error'); return; }
    const photo_key = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setSecondaryPhotos(prev => [...prev, { photo_key, url: URL.createObjectURL(file), file }]);
  }

  function onSecondaryFileRemoved(photoKey) {
    setSecondaryPhotos(prev => {
      const removed = prev.find(p => p.photo_key === photoKey);
      if (removed) URL.revokeObjectURL(removed.url);
      return prev.filter(p => p.photo_key !== photoKey);
    });
  }

  function onBulkFilesSelected(files) {
    const selected = Array.from(files || []);
    const valid = selected.filter(f => f.type.startsWith('image/') && f.size <= MAX_SIZE_BYTES);
    setBulkPhotos(valid);
    if (selected.length && valid.length !== selected.length) pushToast('Some files were skipped. Use image files under 10 MB.', 'info');
  }

  function fileNameWithoutExtension(file) {
    return String(file?.name || 'Guest').replace(/\.[^/.]+$/, '').trim() || 'Guest';
  }

  async function submitRequest(e) {
    e.preventDefault();
    if (requestMode === 'deletion') { await submitDeletionRequest(); return; }
    const cleanedCode = inviteCode.trim();
    if (!/^\d{8}$/.test(cleanedCode)) { pushToast('Invite code must be exactly 8 digits.', 'error'); return; }
    if (!name.trim()) { pushToast('Please fill in your name.', 'error'); return; }
    if (!emailAddress.trim()) { pushToast('Please provide your email address.', 'error'); return; }
    if (referencePhoto && !referenceConsent) { pushToast('Consent is required when uploading a selfie.', 'error'); return; }
    if (referencePhoto && onBehalfRelationship && !subjectName.trim()) { pushToast("Please enter the name of the person in the photo.", 'error'); return; }
    setIsSubmitting(true); pushToast('Submitting…', 'info');
    try {
      const eventInfo = (eventLookupState === 'found' && lookedUpEvent) ? lookedUpEvent : await eventApi.lookupEvent(cleanedCode);
      if (eventInfo.error) { pushToast(eventInfo.error, 'error'); return; }
      const formData = new FormData();
      formData.append('invite_code', cleanedCode); formData.append('name', name.trim()); formData.append('email_address', emailAddress.trim());
      if (contactType === 'whatsapp') { formData.append('whatsapp_number', whatsappNumber.trim()); formData.append('whatsapp_country_code', whatsappCountryCode); }
      else { formData.append('phone_number', phoneNumber.trim()); formData.append('country_code', countryCode); }
      formData.append('notes', notes.trim());
      if (referencePhoto) {
        formData.append('reference_photo', referencePhoto); formData.append('consent_reference_face_match', 'true');
        formData.append('on_behalf_of_relationship', onBehalfRelationship);
        if (onBehalfRelationship) formData.append('subject_name', subjectName.trim());
      }
      formData.append('policy_version', PRIVACY_POLICY_VERSION);
      const res = await eventApi.submitPhotoRequest(eventInfo.event_id, formData);
      if (res.error) { pushToast(res.error, 'error'); return; }
      if (referencePhoto && res.request_id && secondaryPhotos.length) {
        for (const secondary of secondaryPhotos) {
          try {
            await eventApi.addRequestSecondaryPhoto(eventInfo.event_id, res.request_id, secondary.file, { inviteCode: cleanedCode });
          } catch {
            // Best-effort - the primary request already succeeded.
          }
        }
      }
      if (user) { try { await eventApi.joinEvent(cleanedCode); } catch { /* already joined */ } }
      setSubmittedEventId(eventInfo.event_id); setSubmittedEventName(eventInfo.event_name);
      setSubmittedEventAgenda(eventInfo.event_agenda || ''); setSubmittedCount(1); setSubmittedHasSelfie(Boolean(referencePhoto));
      pushToast(referencePhoto ? 'Photo request submitted.' : 'Joined! You can add a selfie later.', 'success');
      setSubmitted(true); if (!user) setShowAccountPrompt(true);
    } catch (err) { pushToast(err.message || 'Failed to submit request.', 'error'); }
    finally { setIsSubmitting(false); }
  }

  async function submitBulkRequests() {
    if (requestMode === 'deletion') { await submitBulkDeletionRequests(); return; }
    const cleanedCode = inviteCode.trim();
    if (!/^\d{8}$/.test(cleanedCode)) { pushToast('Invite code must be exactly 8 digits.', 'error'); return; }
    if (!emailAddress.trim()) { pushToast('Please provide your email address for bulk requests.', 'error'); return; }
    if (!bulkPhotos.length) { pushToast('Please choose photos for bulk upload.', 'error'); return; }
    if (!referenceConsent) { pushToast('Consent is required for bulk reference matching.', 'error'); return; }
    setIsSubmitting(true); setBulkProgress({ active: true, completed: 0, total: bulkPhotos.length, failed: 0 });
    try {
      const eventInfo = (eventLookupState === 'found' && lookedUpEvent) ? lookedUpEvent : await eventApi.lookupEvent(cleanedCode);
      if (eventInfo.error) { pushToast(eventInfo.error, 'error'); return; }
      let failed = 0;
      for (let i = 0; i < bulkPhotos.length; i++) {
        const file = bulkPhotos[i]; const formData = new FormData();
        formData.append('invite_code', cleanedCode); formData.append('name', fileNameWithoutExtension(file)); formData.append('email_address', emailAddress.trim());
        if (contactType === 'whatsapp') { formData.append('whatsapp_number', whatsappNumber.trim()); formData.append('country_code', whatsappCountryCode); }
        else { formData.append('phone_number', phoneNumber.trim()); formData.append('country_code', countryCode); }
        formData.append('notes', notes.trim()); formData.append('reference_photo', file); formData.append('consent_reference_face_match', 'true');
        formData.append('on_behalf_of_relationship', onBehalfRelationship);
        if (onBehalfRelationship) formData.append('subject_name', subjectName.trim());
        formData.append('policy_version', PRIVACY_POLICY_VERSION);
        try { const res = await eventApi.submitPhotoRequest(eventInfo.event_id, formData); if (res.error) failed++; } catch { failed++; }
        setBulkProgress({ active: true, completed: i + 1, total: bulkPhotos.length, failed });
      }
      if (user) { try { await eventApi.joinEvent(cleanedCode); } catch { /* already joined */ } }
      setSubmittedEventId(eventInfo.event_id); setSubmittedEventName(eventInfo.event_name); setSubmittedEventAgenda(eventInfo.event_agenda || '');
      const successCount = bulkPhotos.length - failed;
      if (successCount <= 0) { pushToast('No bulk requests were submitted.', 'error'); return; }
      setSubmittedCount(successCount);
      pushToast(failed ? `${successCount} requests submitted, ${failed} failed.` : 'Bulk requests submitted.', failed ? 'info' : 'success');
      setSubmitted(true); if (!user) setShowAccountPrompt(true);
    } catch (err) { pushToast(err.message || 'Failed.', 'error'); }
    finally { setIsSubmitting(false); setBulkProgress(p => ({ ...p, active: false })); }
  }

  async function submitBulkDeletionRequests() {
    const cleanedCode = inviteCode.trim();
    if (!/^\d{8}$/.test(cleanedCode)) { pushToast('Invite code must be exactly 8 digits.', 'error'); return; }
    if (!emailAddress.trim()) { pushToast('Please fill in your email for bulk deletion requests.', 'error'); return; }
    if (!bulkPhotos.length) { pushToast('Please choose photos for bulk upload.', 'error'); return; }
    if (!referenceConsent) { pushToast('Consent is required for bulk deletion matching.', 'error'); return; }
    setIsSubmitting(true); setBulkProgress({ active: true, completed: 0, total: bulkPhotos.length, failed: 0 });
    try {
      const eventInfo = (eventLookupState === 'found' && lookedUpEvent) ? lookedUpEvent : await eventApi.lookupEvent(cleanedCode);
      if (eventInfo.error) { pushToast(eventInfo.error, 'error'); return; }
      let failed = 0;
      for (let i = 0; i < bulkPhotos.length; i++) {
        const file = bulkPhotos[i]; const formData = new FormData();
        formData.append('invite_code', cleanedCode); formData.append('name', fileNameWithoutExtension(file)); formData.append('email_address', emailAddress.trim());
        formData.append('notes', notes.trim()); formData.append('reference_photo', file); formData.append('consent_deletion_face_match', 'true');
        formData.append('on_behalf_of_relationship', onBehalfRelationship);
        if (onBehalfRelationship) formData.append('subject_name', subjectName.trim());
        formData.append('policy_version', PRIVACY_POLICY_VERSION);
        try { const res = await eventApi.submitDeletionRequest(eventInfo.event_id, formData); if (res.error) failed++; } catch { failed++; }
        setBulkProgress({ active: true, completed: i + 1, total: bulkPhotos.length, failed });
      }
      setSubmittedEventId(eventInfo.event_id); setSubmittedEventName(eventInfo.event_name); setSubmittedEventAgenda(eventInfo.event_agenda || '');
      const successCount = bulkPhotos.length - failed;
      if (successCount <= 0) { pushToast('No bulk deletion requests submitted.', 'error'); return; }
      setSubmittedCount(successCount);
      pushToast(failed ? `${successCount} deletion requests submitted, ${failed} failed.` : 'Bulk deletion requests submitted.', failed ? 'info' : 'success');
      setSubmitted(true);
    } catch (err) { pushToast(err.message || 'Failed.', 'error'); }
    finally { setIsSubmitting(false); setBulkProgress(p => ({ ...p, active: false })); }
  }

  async function submitDeletionRequest() {
    const cleanedCode = inviteCode.trim();
    if (!/^\d{8}$/.test(cleanedCode)) { pushToast('Invite code must be exactly 8 digits.', 'error'); return; }
    if (!name.trim() || !emailAddress.trim()) { pushToast('Please fill in your name and email address.', 'error'); return; }
    if (!referencePhoto) { pushToast('Please upload a reference selfie.', 'error'); return; }
    if (!referenceConsent) { pushToast('Consent is required for face matching.', 'error'); return; }
    if (onBehalfRelationship && !subjectName.trim()) { pushToast("Please enter the name of the person in the photo.", 'error'); return; }
    setIsSubmitting(true);
    try {
      const eventInfo = (eventLookupState === 'found' && lookedUpEvent) ? lookedUpEvent : await eventApi.lookupEvent(cleanedCode);
      if (eventInfo.error) { pushToast(eventInfo.error, 'error'); return; }
      const formData = new FormData();
      formData.append('invite_code', cleanedCode); formData.append('name', name.trim()); formData.append('email_address', emailAddress.trim());
      formData.append('notes', notes.trim()); formData.append('reference_photo', referencePhoto); formData.append('consent_deletion_face_match', 'true');
      formData.append('on_behalf_of_relationship', onBehalfRelationship);
      if (onBehalfRelationship) formData.append('subject_name', subjectName.trim());
      formData.append('policy_version', PRIVACY_POLICY_VERSION);
      const res = await eventApi.submitDeletionRequest(eventInfo.event_id, formData);
      if (res.error) { pushToast(res.error, 'error'); return; }
      setSubmittedEventId(eventInfo.event_id); setSubmittedEventName(eventInfo.event_name); setSubmittedEventAgenda(eventInfo.event_agenda || ''); setSubmittedCount(1);
      pushToast('Deletion request submitted.', 'success'); setSubmitted(true);
    } catch (err) { pushToast(err.message || 'Failed.', 'error'); }
    finally { setIsSubmitting(false); }
  }

  // ── Left panel content ────────────────────────────────────────────────────

  function LeftPanelContent() {
    if (submitted) {
      return (
        <>
          <Typography variant="h3" fontWeight={900} sx={{ lineHeight: 1.15, fontSize: { xs: '1.8rem', md: '2.3rem' }, mb: 1.5, color: 'white' }}>
            {submittedHasSelfie ? 'Request\nSent!' : "You're In!"}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, fontSize: '0.9rem' }}>
            {submittedHasSelfie
              ? `The organiser will match your photos from ${submittedEventName} and deliver them to you.`
              : `You've joined ${submittedEventName}. Check your email for a personal link to access your photos — no account needed.`}
          </Typography>
        </>
      );
    }
    if (requestMode === 'deletion') {
      return (
        <>
          <Typography variant="h3" fontWeight={900} sx={{ lineHeight: 1.15, fontSize: { xs: '1.8rem', md: '2.3rem' }, mb: 1.5, color: 'white' }}>
            Protect<br/>Your Privacy
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, fontSize: '0.9rem' }}>
            Submit a reference selfie and CircleMoments will use face matching to identify your photos so the organiser can withhold them.
          </Typography>
        </>
      );
    }
    return (
      <>
        <Typography variant="h3" fontWeight={900} sx={{ lineHeight: 1.1, fontSize: { xs: '1.9rem', md: '2.5rem' }, mb: 1, color: 'white' }}>
          Get Your<br/>Event Photos
        </Typography>
        <Box component="span" sx={{ display: 'block', background: 'linear-gradient(90deg, #60a5fa 0%, #f472b6 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontWeight: 800, fontSize: { xs: '1rem', md: '1.05rem' }, mb: 2, lineHeight: 1.4 }}>
          Powered by AI face matching
        </Box>
        <Typography sx={{ color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, fontSize: '0.9rem', mb: 3 }}>
          CircleMoments finds every photo of you from the event and delivers them directly - no manual searching, no scrolling.
        </Typography>
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          {['Find every photo of yourself automatically', 'Face-matched across the whole event', 'Delivered directly from the organiser'].map(t => (
            <Box key={t} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
              <CheckCircleIcon sx={{ color: '#80cbc4', fontSize: 17, mt: 0.2, flexShrink: 0 }} />
              <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem', lineHeight: 1.5 }}>{t}</Typography>
            </Box>
          ))}
          <Box sx={{ mt: 4 }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {PHOTO_COLORS.map((bg, i) => (
                <Box key={i} sx={{ width: 58, height: 58, borderRadius: '10px', background: `linear-gradient(135deg, ${bg}40, ${bg}18)`, border: `1px solid ${bg}28`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PhotoLibraryIcon sx={{ color: bg, fontSize: 20, opacity: 0.7 }} />
                </Box>
              ))}
            </Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.7rem', mt: 1.5 }}>Your event photos, matched and delivered</Typography>
          </Box>
        </Box>
      </>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ background: 'linear-gradient(135deg, #060d1f 0%, #0d1b3e 45%, #13235c 80%, #1a1060 100%)', minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
      {/* Background orbs */}
      <Box sx={{ position: 'absolute', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(33,150,243,0.14) 0%, transparent 70%)', top: '-20%', left: '-10%', pointerEvents: 'none' }} />
      <Box sx={{ position: 'absolute', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(199,56,102,0.1) 0%, transparent 70%)', bottom: '-10%', right: '-5%', pointerEvents: 'none' }} />

      {/* Dialogs */}
      <Snackbar
        key={`${snackbar.severity}-${snackbar.message}`}
        open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>

      <Dialog open={!user && showAccountPrompt} onClose={() => setShowAccountPrompt(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Save Your Memories</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            We've emailed you a personal link to access your photos — no account needed. Create a free account with the same email and it'll link automatically, so you can track your request and keep all your event memories in one place.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button onClick={() => setShowAccountPrompt(false)} sx={{ textTransform: 'none' }}>Maybe later</Button>
          <Button variant="contained" onClick={() => navigate('/auth')} sx={{ textTransform: 'none' }}>Create Account / Sign In</Button>
        </DialogActions>
      </Dialog>

      <SelfieCaptureDialog open={selfieCamOpen} onClose={() => setSelfieCamOpen(false)} onCapture={file => { onFileSelected(file); setSelfieCamOpen(false); }} />

      <Dialog open={scannerOpen} onClose={() => setScannerOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Scan Event QR Code</DialogTitle>
        <DialogContent>
          <div className="qr-scanner-frame">
            <video ref={videoRef} className="qr-scanner-video" muted playsInline aria-label="QR scanner camera preview" />
            <div className="qr-scanner-target" aria-hidden="true" />
          </div>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>{scannerStatus || 'Opening camera...'}</Typography>
          <div className="qr-image-upload-row">
            <input ref={qrImageInputRef} type="file" accept="image/*" onChange={e => scanQrImageFile(e.target.files?.[0])} />
            <Button type="button" startIcon={<ImageSearchIcon />} variant="outlined" onClick={() => qrImageInputRef.current?.click()} sx={{ textTransform: 'none' }}>Select QR Image</Button>
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setScannerOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Page content */}
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3, md: 4 } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 3, md: 7 }, alignItems: { xs: 'stretch', md: 'flex-start' }, justifyContent: submitted ? 'center' : 'flex-start' }}>

          {/* ── LEFT PANEL ────────────────────────────────────────────────── */}
          <Box sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0, pt: { xs: 0, md: 2 }, pb: { xs: 0, md: 6 }, display: submitted ? 'none' : 'block' }}>
            <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              {!cameViaLink && (
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(user ? '/dashboard' : '/')} sx={{ mb: 2.5, color: 'rgba(255,255,255,0.5)', textTransform: 'none', pl: 0, '&:hover': { color: 'white', bgcolor: 'transparent' } }}>
                  Back
                </Button>
              )}              
              <LeftPanelContent />
            </motion.div>
          </Box>

          {/* ── RIGHT PANEL ───────────────────────────────────────────────── */}
          <Box sx={{ flex: submitted ? '0 1 auto' : 1, width: { xs: '100%', md: submitted ? 560 : 'auto' }, minWidth: 0, maxWidth: { xs: '100%', md: 560 } }}>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.12 }}>

              {submitted ? (
                <>
                  <div className="join-section-card" style={{ textAlign: 'center' }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'rgba(128,203,196,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                      <CheckCircleIcon sx={{ color: '#80cbc4', fontSize: 32 }} />
                    </Box>
                    <Typography variant="h5" fontWeight={800} mb={1} sx={{ color: 'white' }}>
                      {submittedHasSelfie ? 'Request Submitted!' : "You're Joined!"}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.7, fontSize: '0.92rem', mb: 2 }}>
                      {submittedCount > 1 ? (
                        <>{submittedCount} {requestMode === 'deletion' ? 'deletion requests' : 'photo requests'} for <strong>{submittedEventName}</strong> submitted. The organiser will respond once grouping is complete.</>
                      ) : (
                        <>
                          {requestMode === 'deletion'
                            ? <>Do-not-share request for <strong>{submittedEventName}</strong> received. The organiser is responsible for actioning it.</>
                            : submittedHasSelfie
                              ? <>Photo request for <strong>{submittedEventName}</strong> received. The organiser will match and deliver your photos — we've also emailed you a personal link to check anytime.</>
                              : <>You've joined <strong>{submittedEventName}</strong>. Check your email for a personal link to access your photos as soon as the organiser shares them — no account needed.</>}
                        </>
                      )}
                    </Typography>
                    {user && submittedEventId && (
                      <Button variant="contained" onClick={() => navigate(`/event/${submittedEventId}`)} sx={{ textTransform: 'none' }}>Go to Event Page</Button>
                    )}
                    {!user && !submittedHasSelfie && (
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mt: 2 }}>
                        Create a free account with the same email to add a selfie for automatic matching and keep all your event memories in one place.{' '}
                        <Button size="small" variant="text" onClick={() => navigate('/auth')} sx={{ textTransform: 'none', p: 0, verticalAlign: 'baseline', color: '#90caf9' }}>Sign up</Button>
                      </Typography>
                    )}
                  </div>
                  {submittedEventAgenda && (
                    <div className="join-section-card" style={{ marginTop: 12 }}>
                      <Typography variant="subtitle1" fontWeight={700} mb={1} sx={{ color: 'white' }}>Event agenda</Typography>
                      <MarkdownContent markdown={submittedEventAgenda} />
                    </div>
                  )}
                </>
              ) : (
                <form onSubmit={submitRequest}>
                  {/* Mode toggle */}
                  <div className="join-mode-toggle">
                    <button type="button" className={requestMode === 'photos' ? 'active' : ''} onClick={() => setRequestMode('photos')}>Request my photos</button>
                    <button type="button" className={requestMode === 'deletion' ? 'active' : ''} onClick={() => setRequestMode('deletion')}>Do not share my face</button>
                  </div>

                  {/* Event section */}
                  {eventLookupState === 'loading' ? (
                    <div className="join-section-glass" style={{ justifyContent: 'center' }}>
                      <CircularProgress size={18} sx={{ color: '#90caf9' }} />
                      <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', ml: 1.5 }}>Finding your event…</Typography>
                    </div>
                  ) : eventLookupState === 'found' ? (
                    <div className="join-section-glass">
                      <CheckCircleIcon sx={{ color: '#80cbc4', fontSize: 22, flexShrink: 0 }} />
                      <Typography sx={{ flex: 1, minWidth: 0, color: 'white', fontWeight: 700, fontSize: '0.98rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lookedUpEvent.event_name}</Typography>
                      <Button size="small" onClick={resetEventLookup} sx={{ textTransform: 'none', fontSize: '0.74rem', color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap', minWidth: 0, flexShrink: 0 }}>
                        Not this event?
                      </Button>
                    </div>
                  ) : (
                    <div className="join-section-card">
                      <div className="join-section-header">
                        <SearchIcon sx={{ fontSize: 15, color: '#f48fb1' }} />
                        Event Code
                        <Button type="button" startIcon={<CameraAltIcon />} onClick={() => setScannerOpen(true)} size="small" sx={{ ml: 'auto', textTransform: 'none', color: '#f48fb1', fontWeight: 600, fontSize: '0.78rem', p: '3px 8px', border: '1px solid rgba(217,120,150,0.4)', borderRadius: '8px', minWidth: 0 }}>
                          Scan QR
                        </Button>
                      </div>
                      <input
                        id="invite-code" className="invite-code-input"
                        type="text" value={inviteCode} inputMode="numeric" placeholder="· · · · · · · ·" maxLength={8} required
                        onChange={e => setInviteCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
                      />
                      {eventLookupState === 'error' && (
                        <Typography sx={{ color: '#ef5350', fontSize: '0.8rem', textAlign: 'center', mt: 0.75 }}>
                          Event not found — check your invite code and try again.
                        </Typography>
                      )}
                      <button type="button" className="join-submit-cta" onClick={() => triggerEventLookup(inviteCode)} disabled={inviteCode.length !== 8} style={{ marginTop: 14, fontSize: '0.9rem', padding: '12px' }}>
                        Look Up Event
                      </button>
                    </div>
                  )}

                  {/* Your Details */}
                  <div className="join-section-card">
                    <div className="join-section-header">
                      <PersonIcon sx={{ fontSize: 15, color: '#f48fb1' }} />
                      Your Details
                      {user && <span className="join-section-note">pre-filled from your account</span>}
                    </div>
                    <div className="details-row">
                      <input className="join-input" type="text" placeholder="Full Name" value={name} onChange={e => setName(e.target.value)} required />
                      <input className="join-input" type="email" placeholder="Email address" value={emailAddress} onChange={e => setEmailAddress(e.target.value)} required />
                    </div>
                    {requestMode !== 'deletion' && (
                      <>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'rgba(255,255,255,0.38)' }}>
                          We'll email you a personal link to access your photos &mdash; no account needed.
                        </Typography>
                        <div style={{ marginTop: 10 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <div className="request-mode-switch" style={{ width: 'fit-content', margin: 0 }}>
                              <button type="button" className={contactType === 'phone' ? 'active' : ''} onClick={() => setContactType('phone')}>Phone</button>
                              <button type="button" className={contactType === 'whatsapp' ? 'active' : ''} onClick={() => setContactType('whatsapp')}>WhatsApp</button>
                            </div>
                            <span className="join-section-note" style={{ marginLeft: 0 }}>optional</span>
                          </div>
                          {contactType === 'phone' ? (
                            <div style={{ display: 'flex', gap: 8 }}>
                              <select className="join-input" style={{ flex: '0 0 auto', width: 96 }} value={countryCode} onChange={e => setCountryCode(e.target.value)}>
                                {COUNTRY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                              <input className="join-input" type="tel" placeholder="Phone number" value={phoneNumber} onChange={e => setPhoneNumber(e.target.value.replace(/\D/g, ''))} style={{ flex: 1 }} />
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: 8 }}>
                              <select className="join-input" style={{ flex: '0 0 auto', width: 96 }} value={whatsappCountryCode} onChange={e => setWhatsappCountryCode(e.target.value)}>
                                {COUNTRY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                              <input className="join-input" type="tel" placeholder="WhatsApp number" value={whatsappNumber} onChange={e => setWhatsappNumber(e.target.value.replace(/\D/g, ''))} style={{ flex: 1 }} />
                            </div>
                          )}
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'rgba(255,255,255,0.38)' }}>
                            Optional &mdash; used for SMS/WhatsApp updates only
                          </Typography>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Reference Selfie */}
                  <div className="join-section-card">
                    <div className="join-section-header">
                      <CameraAltIcon sx={{ fontSize: 15, color: '#f48fb1' }} />
                      {requestMode === 'deletion' ? 'Reference Selfie' : 'Reference Selfie'}
                      {requestMode === 'photos' && <span className="join-section-note">optional</span>}
                      {requestMode === 'deletion' && <span className="join-section-note required-badge">required</span>}
                    </div>
                    {requestMode === 'photos' && (
                      <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', mb: 1.5, mt: '-4px' }}>
                        Helps the organiser match your photos using AI. You can add it later from the event page.
                      </Typography>
                    )}
                    {(referencePhoto || requestMode === 'deletion') && (
                      <>
                        <FormControlLabel
                          sx={{ alignItems: 'flex-start', my: 1, mb: 0 }}
                          control={<Checkbox checked={referenceConsent} onChange={e => setReferenceConsent(e.target.checked)} required={Boolean(referencePhoto) || requestMode === 'deletion'} size="small" sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: '#80cbc4' } }} />}
                          label={
                            <Typography variant="body2" sx={{ fontSize: '0.78rem', lineHeight: 1.55, color: 'rgba(255,255,255,0.5)' }}>
                              {onBehalfRelationship === 'minor' ? (
                                <>
                                  I confirm I am the parent or legal guardian of the person named above and have the authority to consent, on their behalf, to CircleMoments {requestMode === 'deletion'
                                    ? 'storing this selfie for up to 30 days and using face matching to identify their photos so the organiser can withhold them.'
                                    : 'storing this selfie and comparing it with event face groups to find their photos.'} I can withdraw this request later.{' '}
                                  <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#90caf9' }}>Read our privacy &amp; consent terms</a>.
                                </>
                              ) : onBehalfRelationship === 'other_adult' ? (
                                <>
                                  I confirm that the person named above is aware of this submission and has agreed to it, and that I have their permission to act on their behalf for CircleMoments {requestMode === 'deletion'
                                    ? 'storing this selfie for up to 30 days and using face matching to identify their photos so the organiser can withhold them.'
                                    : 'storing this selfie and comparing it with event face groups to find their photos.'} I can withdraw this request later.{' '}
                                  <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#90caf9' }}>Read our privacy &amp; consent terms</a>.
                                </>
                              ) : (
                                requestMode === 'deletion'
                                  ? 'I agree that CircleMoments may store this selfie for up to 30 days and use face matching to identify photos linked to my face. The organiser is responsible for actioning these photos.'
                                  : 'I agree that CircleMoments may store this selfie and compare it with event face groups to find my photos. I can delete my request later.'
                              )}
                            </Typography>
                          }
                        />

                        <Box
                          onClick={() => setOnBehalfExpanded(v => !v)}
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', mt: 0, mb: 2, width: 'fit-content' }}
                        >
                          <Typography variant="body2" sx={{ fontSize: '0.78rem', color: '#90caf9', textDecoration: 'underline' }}>
                            Are you submitting this on behalf of someone else?
                          </Typography>
                          {onBehalfExpanded
                            ? <ExpandLessIcon sx={{ fontSize: 16, color: '#90caf9' }} />
                            : <ExpandMoreIcon sx={{ fontSize: 16, color: '#90caf9' }} />}
                        </Box>

                        {onBehalfExpanded && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mb: onBehalfRelationship ? 1.5 : 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Checkbox
                                checked={onBehalfRelationship === 'minor'}
                                onChange={e => setOnBehalfRelationship(e.target.checked ? 'minor' : '')}
                                size="small"
                                sx={{ p: 0.5, color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: '#80cbc4' } }}
                              />
                              <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)' }}>
                                On behalf of a minor (e.g. my child)
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Checkbox
                                checked={onBehalfRelationship === 'other_adult'}
                                onChange={e => setOnBehalfRelationship(e.target.checked ? 'other_adult' : '')}
                                size="small"
                                sx={{ p: 0.5, color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: '#80cbc4' } }}
                              />
                              <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.5)' }}>
                                On behalf of another adult (e.g. a family member), with their agreement
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {onBehalfRelationship && (
                          <input
                            className="join-input"
                            type="text"
                            placeholder={onBehalfRelationship === 'minor' ? "Name of the child in the photo" : "Name of the person in the photo"}
                            value={subjectName}
                            onChange={e => setSubjectName(e.target.value)}
                            required={Boolean(onBehalfRelationship)}
                            style={{ marginTop: 4, marginBottom: 14 }}
                          />
                        )}
                      </>
                    )}
                    {previewUrl ? (
                      <div className="selfie-preview-block">
                        <img src={previewUrl} alt="Reference selfie" className="reference-preview" />
                        <button type="button" className="selfie-change-btn" onClick={() => setReferencePhoto(null)}>Change photo</button>
                      </div>
                    ) : (
                      <div className="selfie-capture-area">
                        <div className="selfie-capture-row">
                          <button type="button" className="selfie-open-cam-btn" onClick={() => setSelfieCamOpen(true)}>
                            <CameraAltIcon sx={{ fontSize: '1.1rem' }} />
                            {requestMode === 'deletion' ? 'Take Reference Selfie *' : 'Take Selfie'}
                          </button>
                          <label className="selfie-upload-icon-btn" title="Upload from gallery">
                            <input type="file" accept="image/*" onChange={e => onFileSelected(e.target.files?.[0])} />
                            <FileUploadOutlinedIcon fontSize="small" />
                          </label>
                        </div>
                        <span className="selfie-capture-hint">or tap the upload icon to choose from gallery</span>
                      </div>
                    )}
                    {requestMode !== 'deletion' && referencePhoto && (
                      <ReferencePhotoRow
                        photos={secondaryPhotos}
                        onAdd={onSecondaryFileAdded}
                        onRemove={onSecondaryFileRemoved}
                        label="Additional reference photos (optional) - more angles help matching"
                      />
                    )}
                    {/* Notes inside selfie card */}
                    <textarea
                      className="join-input join-notes-input"
                      placeholder="Notes for the organiser (optional)"
                      value={notes} onChange={e => setNotes(e.target.value)}
                      maxLength={2000} rows={2}
                      style={{ marginTop: 12 }}
                    />
                  </div>

                  {/* Submit CTA */}
                  {requestMode !== 'deletion' && (
                    <Typography sx={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.5, mb: 1.25, textAlign: 'center' }}>
                      Already submitted? Don't resubmit - use the email link or log in with that email to view photos. Different name/selfie? Go ahead.
                    </Typography>
                  )}
                  <button type="submit" className="join-submit-cta" disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting…' : requestMode === 'deletion' ? 'Submit Do-Not-Share Request' : (referencePhoto ? 'Request My Photos' : 'Join Event')}
                  </button>

                  {/* Bulk upload — collapsed */}
                  <div style={{ marginTop: 8 }}>
                    <button type="button" className="bulk-toggle-btn" onClick={() => setBulkOpen(v => !v)}>
                      <span style={{ flex: 1, textAlign: 'left' }}>
                        {requestMode === 'deletion' ? 'Bulk Deletion Requests' : 'Bulk Upload Requests'}
                        <Typography component="span" variant="caption" sx={{ ml: 1, color: 'rgba(255,255,255,0.25)', fontWeight: 400 }}>for organisations</Typography>
                      </span>
                      {bulkOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </button>
                    {bulkOpen && (
                      <div className="join-section-card">
                        <Typography variant="body2" sx={{ mb: 1.5, fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)' }}>
                          Select multiple reference photos. Each file name becomes the request name.
                        </Typography>
                        <label className="bulk-upload-drop">
                          <input type="file" accept="image/*" multiple onChange={e => onBulkFilesSelected(e.target.files)} />
                          <strong>{bulkPhotos.length ? `${bulkPhotos.length} files selected` : 'Choose bulk photos'}</strong>
                          <span>{requestMode === 'deletion' ? 'Same invite code and email will be used.' : `Same invite code, email, and ${contactType === 'whatsapp' ? 'WhatsApp' : 'phone'} will be used.`}</span>
                        </label>
                        {bulkPhotos.length > 0 && (
                          <div className="bulk-file-list">
                            {bulkPhotos.slice(0, 6).map(file => <span key={`${file.name}-${file.size}`}>{fileNameWithoutExtension(file)}</span>)}
                            {bulkPhotos.length > 6 && <span>+{bulkPhotos.length - 6} more</span>}
                          </div>
                        )}
                        {bulkProgress.total > 0 && (
                          <div className="bulk-progress-wrap">
                            <LinearProgress variant="determinate" value={(bulkProgress.completed / bulkProgress.total) * 100} />
                            <span>{bulkProgress.completed} of {bulkProgress.total} submitted{bulkProgress.failed ? `, ${bulkProgress.failed} failed` : ''}</span>
                          </div>
                        )}
                        <button type="button" className="join-submit-cta" onClick={submitBulkRequests} disabled={isSubmitting || !bulkPhotos.length} style={{ marginTop: 14 }}>
                          {bulkProgress.active ? 'Submitting…' : requestMode === 'deletion' ? 'Submit Bulk Deletion Requests' : 'Submit Bulk Requests'}
                        </button>
                      </div>
                    )}
                  </div>

                  <Box sx={{ pb: 4 }} />
                </form>
              )}
            </motion.div>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
